import { inject, Injectable, Injector } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';

import { ClientLogApiSender, ClientLogBatch, ClientLogSendOptions } from '@efficiently/client-core-logger';

import { SEND_CLIENT_LOG_BATCH_MUTATION } from '../queries';

/**
 * The batch goes out as collected: `ClientLogLevel` publishes each severity under its own runtime
 * name, so `trace` is both what the logger recorded and what the mutation accepts. Nothing here
 * translates the vocabulary.
 */
@Injectable()
export class ClientLogApiSenderService implements ClientLogApiSender {
	private readonly injector = inject(Injector);

	public async send(batch: ClientLogBatch, options?: ClientLogSendOptions): Promise<void> {
		const apollo = this.injector.get(Apollo, null);

		if (!apollo) {
			throw new Error('Apollo provider is required for client logger GraphQL transport');
		}

		const response = await firstValueFrom(
			apollo.mutate({
				mutation: SEND_CLIENT_LOG_BATCH_MUTATION,
				variables: {
					payload: batch,
				},
				context: {
					skipClientLogger: true,
					skipGlobalErrorHandler: true,
					// A background diagnostics write must never trigger a token refresh or a forced logout.
					skipRefreshOnUnauthorized: true,
					// The declared per-operation contract (`http.link.ts`), not `fetchOptions`. Keepalive
					// requests share a 64 KiB per-origin body budget, so an unload batch competes with
					// every other keepalive write on the page. `keepaliveMaxBatchSize` is what keeps it
					// small — the interval path is free to send a much larger batch.
					keepalive: options?.keepalive === true,
				},
			}),
		);

		if (!response.data?.sendClientLogBatch) {
			throw new Error('Client logger GraphQL mutation failed');
		}
	}
}
